import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, ClipboardList, TrendingUp, Calendar, ChevronRight, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';
import Sidebar from '../components/Sidebar';

const EarningsPage = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [earningsByJob, setEarningsByJob] = useState([]);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser || userData?.role !== 'serviceProvider') {
      setLoading(false);
      return;
    }

    const unsubscribe = ticketService.subscribeToTickets(
      currentUser.uid,
      'serviceProvider',
      (ticketList) => {
        setTickets(ticketList || []);
      }
    );

    return unsubscribe;
  }, [currentUser, userData?.role]);

  useEffect(() => {
    if (tickets.length === 0) {
      setEarningsByJob([]);
      setRecentExpenses([]);
      setTotalEarnings(0);
      setLoading(false);
      return;
    }

    const loadEarnings = async () => {
      setLoading(true);
      let total = 0;
      const byJob = [];
      const allExpenses = [];

      for (const ticket of tickets) {
        const expenses = await expenseService.getExpensesByTicket(ticket.id);
        const jobTotal = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
        total += jobTotal;
        if (jobTotal > 0) {
          byJob.push({
            ticketId: ticket.id,
            title: ticket.title || 'Untitled Job',
            total: jobTotal,
            count: expenses.length
          });
          expenses.forEach(e => allExpenses.push({
            ...e,
            ticketTitle: ticket.title || 'Untitled Job',
            ticketId: ticket.id
          }));
        }
      }

      byJob.sort((a, b) => b.total - a.total);
      allExpenses.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setTotalEarnings(total);
      setEarningsByJob(byJob);
      setRecentExpenses(allExpenses.slice(0, 15));
      setLoading(false);
    };

    loadEarnings();
  }, [tickets]);

  if (loading && tickets.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-slate-600 mx-auto" />
          <p className="mt-4 text-sm font-medium text-slate-600">Loading earnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          <header className="bg-white border-b border-slate-200 sticky top-0 z-10 w-full">
            <div className="flex items-center justify-between px-8 py-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-slate-800">{t('dashboard.serviceProvider.earnings.title')}</h1>
                  <p className="text-slate-500 text-sm">{t('dashboard.serviceProvider.earnings.subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-slate-500 pl-3 border-l border-slate-200">
                  {new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                </div>
                <span className="px-2.5 py-1 bg-slate-100 rounded-md text-xs font-medium text-slate-700 capitalize">{userData?.role}</span>
              </div>
            </div>
          </header>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-800 rounded-lg border border-slate-700 p-5 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-300 uppercase tracking-wide">{t('dashboard.serviceProvider.earnings.totalEarnings')}</p>
                    <p className="text-2xl font-semibold tabular-nums mt-1">${totalEarnings.toFixed(2)}</p>
                  </div>
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Jobs with earnings</p>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums mt-1">{earningsByJob.length}</p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg per job</p>
                    <p className="text-2xl font-semibold text-slate-800 tabular-nums mt-1">
                      ${earningsByJob.length ? (totalEarnings / earningsByJob.length).toFixed(2) : '0.00'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  Earnings by job
                </h2>
                {earningsByJob.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No earnings yet. Complete jobs and add expenses to see earnings here.
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {earningsByJob.map((job) => (
                      <li key={job.ticketId}>
                        <Link
                          to={`/tickets/${job.ticketId}`}
                          className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors group"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 bg-slate-100 rounded-md flex items-center justify-center shrink-0">
                              <Wrench className="w-4 h-4 text-slate-600" strokeWidth={1.75} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate group-hover:text-slate-600">{job.title}</p>
                              <p className="text-xs text-slate-500">{job.count} expense{job.count !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-semibold text-slate-800">${job.total.toFixed(2)}</span>
                            <ChevronRight className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-slate-600" strokeWidth={1.75} />
                  Recent expenses
                </h2>
                {recentExpenses.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No expenses logged yet.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {recentExpenses.map((exp) => (
                      <li key={exp.id}>
                        <Link
                          to={`/tickets/${exp.ticketId}`}
                          className="flex items-center justify-between py-2.5 px-3 rounded-md hover:bg-slate-50 transition-colors text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-slate-800 truncate">{exp.description || 'Expense'}</p>
                            <p className="text-xs text-slate-500 truncate">{exp.ticketTitle} · {exp.createdAt?.toDate?.()?.toLocaleDateString() || '—'}</p>
                          </div>
                          <span className="text-sm font-medium text-slate-800 shrink-0 ml-2">${(exp.amount || 0).toFixed(2)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarningsPage;
